"use client";

import { useEffect, useState } from "react";
import { useTrackingData } from "@/contexts/tracking/tracking.context";
import { useInViewTracking } from "@/hooks/tracking/in-view/use-in-view-tracking.hook";
import { useJpmuid } from "@/hooks/tracking/jpmuid/use-jpmuid.hook";
import { useAuthentication } from "@/vendors/authentication/hooks/use-authentication.hook";
import { useCookiebotConsent } from "@/vendors/Cookiebot/_hooks/use-cookiebot-consent.hook";
import { mapToSnowplowRecommendedArticlePayload } from "@/vendors/Snowplow/mappers/map-to-snowplow-recommended-article-payload";
import { getUniqueDomPath } from "@/vendors/Snowplow/utilities/get-unique-dom-path";
const whitespaceRegex = /\s+/;
export type SnowplowRecommendationInViewTrackingProps = {
  readonly articleIds: ReadonlyArray<number>;
  readonly threshold?: number;
  readonly trackingId: null | string;
};
type ElementInfo = {
  readonly elementClassNames: ReadonlyArray<string>;
  readonly uniqueDomPath: string;
  readonly tagName: string;
};
export function useSnowplowRecommendationInViewTracking<ReferenceType extends HTMLElement>({
  articleIds,
  threshold = 0.8,
  trackingId
}: SnowplowRecommendationInViewTrackingProps) {
  const [elementInfo, setElementInfo] = useState<ElementInfo | null>(null);
  const {
    trackingData
  } = useTrackingData();
  const {
    jpmuid
  } = useJpmuid();
  const {
    user
  } = useAuthentication();
  const consent = useCookiebotConsent();
  const recommendedArticleContext = trackingData !== null && elementInfo !== null ? mapToSnowplowRecommendedArticlePayload({
    articleIds,
    elementClassNames: elementInfo.elementClassNames,
    jpmuid,
    tagName: elementInfo.tagName,
    trackingData,
    trackingId,
    uniqueDomPath: elementInfo.uniqueDomPath,
    user
  }) : null;
  const ref = useInViewTracking<ReferenceType>({
    intersectionObserverOptions: {
      threshold
    },
    model: {
      action: "elements_in_view",
      category: "user_activity",
      consent,
      context: recommendedArticleContext,
      hasTrackingEnabled: trackingId !== null && trackingData !== null
    }
  });
  useEffect(() => {
    if (ref.current) {
      const element = ref.current;
      const classNames = element.className.split(whitespaceRegex).filter(Boolean);
      const tagName = element.tagName.toLowerCase();
      const uniqueDomPath = getUniqueDomPath(element);
      setElementInfo({
        elementClassNames: classNames,
        tagName,
        uniqueDomPath
      });
    }
  }, [ref.current]);
  return ref;
}